import { ValidationError } from 'class-validator';
import { useState } from 'react';
import { setErrorToast } from './errorToast.util';
import { useAppDispatch } from 'redux/storeHooks';
import { makeRequest } from './apiRequest';
import { ApiRequestParams } from './apiRequestParams';
import { EndpointDefinition } from './endpointDefinition';

export interface ErrorResponse {
  message: string;
  errors: ValidationError[];
  name: string;
  code: number;
  userMessage?: string;
}

export const useApiCall = <
  RESPONSE extends object,
  BODY extends object,
  PARAMS extends ApiRequestParams
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>
): [
  RESPONSE,
  boolean,
  (requestConfig: { params?: PARAMS; body?: BODY }) => void,
  ErrorResponse
] => {
  const [requestIdObj] = useState({ requestId: 1 });
  const [isLoading, setIsLoading] = useState<boolean>(null);
  const [response, setResponse] = useState<RESPONSE>(null);
  const [errorResponse, setErrorResponse] = useState<ErrorResponse>(null);

  const dispatch = useAppDispatch();

  const makeApiCall = (requestConfig: {
    params?: PARAMS;
    body?: BODY;
    defaults?: ApiRequestParams;
  }): void => {
    requestIdObj.requestId = requestIdObj.requestId + 1;
    const thisRequestId = requestIdObj.requestId;

    setResponse(null);
    setIsLoading(true);
    const requestPromise = makeRequest(endpoint, requestConfig);
    requestPromise
      .then(response => {
        if (thisRequestId === requestIdObj.requestId) {
          // Only set Response if it is the most recent one
          setResponse(response);
        }
      })
      .finally(() => {
        if (thisRequestId === requestIdObj.requestId) {
          setIsLoading(false);
        }
      })
      .catch(error => {
        const errorResponse: ErrorResponse = {
          message: error.message,
          errors: error.errors,
          name: error.name,
          code: error.body['statusCode'],
          userMessage: error.body['userMessage'],
        };
        setErrorToast(error, dispatch);
        setErrorResponse(errorResponse);
      });
  };

  return [response, isLoading, makeApiCall, errorResponse];
};
